
import React, { useState, useRef } from 'react';
import { AppSettings, Report } from '../types';
import { Building, FileText, UserCog, Plus, Mail, X, Shield, RefreshCw, Loader2, Upload, FileJson } from 'lucide-react';
import { Button } from './Button';
import { syncRolesFromAllProjects } from '../services/cloudService';

interface Props {
  settings: AppSettings;
  onSave: (settings: AppSettings) => void;
  onClose: () => void;
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  onMergeReport?: (report: Report) => void; 
}

export const SettingsForm: React.FC<Props> = ({ settings, onSave, onMergeReport }) => {
  const [newRole, setNewRole] = useState('');
  const [isSyncingRoles, setIsSyncingRoles] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (field: keyof AppSettings, value: any) => {
    onSave({ ...settings, [field]: value });
  };

  const addRole = () => {
      if (newRole && newRole.trim()) {
          const normalized = newRole.trim();
          const currentRoles = settings.customRoles || [];
          const exists = currentRoles.some(r => r.toLowerCase() === normalized.toLowerCase());
          
          if (!exists) {
            handleChange('customRoles', [...currentRoles, normalized].sort());
            setNewRole('');
          } else {
            alert("Diese Rolle existiert bereits.");
          }
      }
  };

  const removeRole = (role: string) => {
      handleChange('customRoles', (settings.customRoles || []).filter(r => r !== role));
  };

  const handleSyncRoles = async () => {
      setIsSyncingRoles(true);
      try {
          const updatedRoles = await syncRolesFromAllProjects();
          // Filter out existing standard roles during sync
          const filtered = updatedRoles.filter(r => !['Bauherr', 'SiGeKo', 'Bauleitung', 'Architekt', 'Statiker', 'Gewerk', 'Fachplaner', 'Behörde'].includes(r));
          handleChange('customRoles', filtered);
          alert(`${filtered.length} benutzerdefinierte Rollen wurden synchronisiert.`);
      } catch (e: any) {
          alert("Fehler beim Synchronisieren: " + e.message);
      } finally {
          setIsSyncingRoles(false);
      }
  };
  
  const handleJsonUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onMergeReport) return;
      
      setIsImporting(true);
      const reader = new FileReader();
      reader.onload = (event) => {
          try {
              const data = JSON.parse(event.target?.result as string);
              if (data && data.metadata && data.items) {
                  onMergeReport(data as Report);
              } else {
                  alert("Ungültiges Dateiformat.");
              }
          } catch {
              alert("Fehler beim Lesen der Datei.");
          } finally {
              setIsImporting(false);
          }
      };
      reader.readAsText(file);
      e.target.value = '';
  };

  return (
    <div data-glass-view="berichtseinstellungen" className="max-w-4xl mx-auto animate-fade-in pb-20">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
             <div>
                <h2 className="text-blue-800 font-bold text-lg flex items-center">
                <Building className="w-5 h-5 mr-2 text-blue-600" />
                Berichts-Einstellungen
                </h2>
                <p className="text-xs text-slate-500 mt-1">Konfiguration für Dateinamen und Vorlagen.</p>
            </div>
        </div>
      
        <div className="p-6 space-y-8">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-slate-800">Globale Firmen-Daten</h3>
                    <p className="text-xs text-slate-600 mt-1">
                        Firmenname, Logo, Adresse und der App-Modus werden zentral unter
                        <span className="font-bold"> Verwaltung → Firmeneinstellungen</span> verwaltet.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                       <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 flex items-center">
                         <FileText className="w-4 h-4 mr-1"/> Dateinamen-Muster (Speichern)
                       </label>
                       <input 
                          type="text" 
                          value={settings.filenameTemplate || ''}
                          onChange={(e) => handleChange('filenameTemplate', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="{datum}_{projekt}_Bericht_Nr.{nr}"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Verfügbare Platzhalter: {'{projekt}'}, {'{datum}'}, {'{nr}'}, {'{ort}'}
                      </p>
                    </div>
                    
                    {onMergeReport && (
                        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm">
                           <label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider block mb-2 flex items-center">
                             <FileJson className="w-4 h-4 mr-1"/> Bericht Importieren (Merge)
                           </label>
                           <p className="text-[11px] text-indigo-600/80 mb-3 leading-relaxed">
                               Laden Sie einen alten JSON-Bericht hoch, um Mängel, Checklisten und Gefahren zu übernehmen.
                           </p>
                           <Button 
                                variant="secondary" 
                                onClick={() => fileInputRef.current?.click()} 
                                disabled={isImporting}
                                className="w-full text-xs justify-center bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                           >
                               {isImporting ? <Loader2 className="w-3 h-3 animate-spin mr-2"/> : <Upload className="w-3 h-3 mr-2"/>}
                               Datei auswählen...
                           </Button>
                           <input type="file" ref={fileInputRef} accept=".json" className="hidden" onChange={handleJsonUpload} />
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex justify-between items-center mb-2">
                             <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center">
                                <UserCog className="w-4 h-4 mr-1"/> Benutzerdefinierte Rollen
                            </label>
                            <button 
                                onClick={handleSyncRoles} 
                                disabled={isSyncingRoles}
                                className="text-[11px] bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1 transition-colors"
                            >
                                {isSyncingRoles ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                                Sync
                            </button>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3 max-h-32 overflow-y-auto p-1 custom-scrollbar">
                            {settings.customRoles?.map(role => (
                                <span key={role} className="inline-flex items-center px-2 py-1 rounded bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700">
                                    {role}
                                    <button onClick={() => removeRole(role)} className="ml-1 text-slate-400 hover:text-red-500"><X className="w-3 h-3"/></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                placeholder="Neue Rolle..."
                                className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                onKeyDown={(e) => e.key === 'Enter' && addRole()}
                            />
                            <Button variant="secondary" onClick={addRole} className="px-2 py-1 h-auto text-xs"><Plus className="w-4 h-4"/></Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <h3 className="text-blue-800 font-bold text-md flex items-center mb-4">
                <Mail className="w-5 h-5 mr-2 text-indigo-600" />
                E-Mail Vorlagen
              </h3>
              
              <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Betreff</label>
                      <input 
                          type="text" 
                          value={settings.emailSubjectTemplate || ''}
                          onChange={(e) => handleChange('emailSubjectTemplate', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="SiGeKo Begehungsprotokoll Nr. {nr} - {projekt}"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">E-Mail Text</label>
                      <textarea 
                          rows={6}
                          value={settings.emailBodyTemplate || ''}
                          onChange={(e) => handleChange('emailBodyTemplate', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Sehr geehrte Damen und Herren..."
                      />
                    </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};
